import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import { useApi } from "../api";
import { botAlertsGranted, requestBotAlerts, useTelegram } from "../tg";
import { Icon } from "../Icon";
import { Photo } from "../Photo";
import { SkillBadge } from "../SkillBadge";
import { useI18n } from "../i18n";
import { reverseGeocode } from "../geo";
import { effectiveSkillLevel, hasSkillBadge } from "../lib/skill";
import { isEvalDone } from "../lib/eval-done";
import { FILTER_GAMES_BY_CITY } from "../lib/city-filter";
import { isGameLive } from "../lib/game-status";
import { GameCard } from "./GameCard";
import "./Home.css";

function hasOnboardedLocally(): boolean {
  try {
    return localStorage.getItem("volley:onboarded:v1") === "1";
  } catch {
    return false;
  }
}

const STORAGE_LAT = 'volley:lat:v1';
const STORAGE_LNG = 'volley:lng:v1';
const STORAGE_CITY = 'volley:city:v1';

function getStoredCity(): string | null {
  try {
    return localStorage.getItem(STORAGE_CITY);
  } catch {
    return null;
  }
}

export function HomePage() {
  const api = useApi();
  const { user, webApp, photoUrl } = useTelegram();
  const { t } = useI18n();
  const meQ = useQuery(["me"], () => api.me(), {
    refetchOnMount: "always",
    staleTime: 0,
  });
  const cityQ = useQuery(["default-city"], () => api.defaultCity());
  // Prefer profile city so Home matches games the user just created
  // (venues are stamped with me.city). Server expands aliases
  // (Poznań / Познань) so DEFAULT_CITY and profile spellings both match.
  // FILTER_GAMES_BY_CITY is temporarily off — all cities are listed.
  const filterCity = meQ.data?.city || cityQ.data?.city || undefined;
  // Query key includes a version suffix so changes to the games payload
  // shape (e.g. the v4 fields `playType` / `participants.evaluatedSkillLevel`)
  // automatically invalidate any cached entries from an older build without
  // requiring a hard reload.
  const gamesQ = useQuery(
    ["games", FILTER_GAMES_BY_CITY ? filterCity : "ALL", "HOME", "v5"],
    () =>
      api.listGames(
        FILTER_GAMES_BY_CITY && filterCity ? { city: filterCity } : {},
      ),
    {
      enabled: FILTER_GAMES_BY_CITY
        ? !!cityQ.data || !!meQ.data?.city
        : !!meQ.data || cityQ.isFetched,
    },
  );
  const pendingEvalQ = useQuery(
    ["evaluations", "pending"],
    () => api.listPendingEvaluations(),
    {
      enabled: !!meQ.data?.id,
      staleTime: 15_000,
      refetchOnWindowFocus: true,
    },
  );
  const rateGames = useMemo(
    () =>
      (pendingEvalQ.data?.games ?? []).filter((g) => !isEvalDone(g.id)).slice(0, 3),
    [pendingEvalQ.data],
  );
  const navigate = useNavigate();

  const [locating, setLocating] = useState(false);
  const [alertsOn, setAlertsOn] = useState(botAlertsGranted);
  const [storedCity, setStoredCity] = useState<string | null>(() => getStoredCity());
  const firstName = meQ.data?.firstName ?? user?.first_name ?? "friend";
  const city =
    (storedCity && meQ.data?.city === cityQ.data?.city ? storedCity : null) ??
    meQ.data?.city ??
    storedCity ??
    cityQ.data?.city ??
    "your city";
  const openGames = (gamesQ.data ?? []).filter((g) => g.status === "OPEN");
  // Upcoming section: only joinable/live lobbies — never FINISHED.
  // Live games (kickoff passed) float to the top.
  const nextGames = useMemo(() => {
    const list = (gamesQ.data ?? []).filter(
      (g) => g.status === "OPEN" || g.status === "FULL",
    );
    const now = Date.now();
    return [...list]
      .sort((a, b) => {
        const aLive = isGameLive(a) ? 0 : 1;
        const bLive = isGameLive(b) ? 0 : 1;
        if (aLive !== bLive) return aLive - bLive;
        return new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
      })
      .filter((g) => {
        // Drop anything past the auto-finish horizon just in case.
        return new Date(g.startAt).getTime() >= now - 5 * 60 * 60 * 1000;
      })
      .slice(0, 3);
  }, [gamesQ.data]);
  const needsOnboarding =
    meQ.data != null &&
    effectiveSkillLevel(meQ.data) == null &&
    !hasOnboardedLocally();

  // Try to geolocate once on first mount, if user has not stored coords yet.
  useEffect(() => {
    if (!navigator.geolocation) return;
    try {
      if (localStorage.getItem(STORAGE_CITY)) return;
    } catch {}
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          localStorage.setItem(STORAGE_LAT, String(pos.coords.latitude));
          localStorage.setItem(STORAGE_LNG, String(pos.coords.longitude));
        } catch {}
        try {
          const patch: Record<string, unknown> = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          // Try to refresh the city too so the Home header doesn't keep
          // showing the old default ("Kyiv") next to the real coordinates.
          const city = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
          if (city) {
            patch.city = city;
            try {
              localStorage.setItem(STORAGE_CITY, city);
            } catch {}
            setStoredCity(city);
          }
          await api.updateMe(patch as any);
          qcRefetchMe();
        } catch {}
      },
      () => undefined,
      { timeout: 6000, enableHighAccuracy: false, maximumAge: 600_000 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function qcRefetchMe() {
    // small wrapper — kept inline to avoid import bloat for a single call
    meQ.refetch?.();
  }

  const requestLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          localStorage.setItem(STORAGE_LAT, String(pos.coords.latitude));
          localStorage.setItem(STORAGE_LNG, String(pos.coords.longitude));
        } catch {}
        try {
          const patch: Record<string, unknown> = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          // Best-effort: figure out the city name so the city field updates
          // too — otherwise the user sees the old default ("Kyiv") next to
          // their real coordinates.
          const city = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
          if (city) {
            patch.city = city;
            try {
              localStorage.setItem(STORAGE_CITY, city);
            } catch {}
            setStoredCity(city);
          }
          await api.updateMe(patch as any);
        } catch {
          // Even if reverse geocoding or the PATCH fails, we still
          // saved the coords to localStorage; the city will fall back to
          // whatever the server already had.
        } finally {
          setLocating(false);
          meQ.refetch();
          cityQ.refetch();
          gamesQ.refetch();
        }
      },
      () => setLocating(false),
      { timeout: 8000, enableHighAccuracy: false, maximumAge: 60_000 },
    );
  };

  const goCreate = () => {
    webApp?.HapticFeedback?.impactOccurred?.("medium");
    navigate("/create");
  };
  const goGames = () => navigate("/games");
  const goWelcome = () => {
    webApp?.HapticFeedback?.impactOccurred?.("light");
    navigate("/welcome");
  };

  return (
    <div className="home">
      <header className="home-hero">
        <div className="home-hero-text">
          <div className="home-hero-eyebrow">
            <Icon name="map-pin" size={12} />
            <span>{city}</span>
            <button
              type="button"
              className="home-hero-locateBtn"
              onClick={requestLocation}
              disabled={locating}
              aria-label="Use my location"
              data-analytics-label="home-use-location"
            >
              <Icon name={locating ? "loading" : "target"} size={11} />
            </button>
          </div>
          <div className="home-hero-titleRow">
            <h1 className="home-hero-title">
              {t('home.hello', { name: firstName })}
            </h1>
            {meQ.data?.isSuperAdmin && (
              <span
                className="home-hero-adminPill"
                title={t('profile.status.admin')}
                aria-label={t('profile.status.admin')}
                data-analytics-label="home-admin-pill"
              >
                <Icon name="crown" size={11} />
                <span>{t('profile.status.admin')}</span>
              </span>
            )}
          </div>
          <div className="home-hero-meta">
            {hasSkillBadge(meQ.data) ? (
              // Display-only. Self-level is set once during first onboarding;
              // afterwards peer ratings adjust `evaluatedSkillLevel`.
              <div className="home-hero-skill" aria-label={t('profile.skill')}>
                <SkillBadge
                  level={effectiveSkillLevel(meQ.data)}
                  wheelchair={!!meQ.data?.showWheelchairBadge}
                  size="sm"
                />
              </div>
            ) : !hasOnboardedLocally() ? (
              <button
                type="button"
                className="home-hero-skillPick"
                onClick={goWelcome}
                data-analytics-label="home-skill-pick"
              >
                <Icon name="award-01" size={11} />
                <span>{t('home.pickYourLevel')}</span>
              </button>
            ) : null}
            <p className="home-hero-sub">
              {openGames.length > 0
                ? t('home.openGamesInCity', { count: openGames.length })
                : t('home.noGamesSub')}
            </p>
          </div>
        </div>
        <div className="home-hero-photoWrap">
          <Photo
            src={meQ.data?.photoUrl ?? photoUrl ?? null}
            name={firstName}
            size={56}
          />
        </div>
      </header>

      {webApp && !alertsOn && (
        <button
          type="button"
          className="home-onboardBanner"
          onClick={() => {
            webApp.HapticFeedback?.impactOccurred?.('light');
            void requestBotAlerts(cityQ.data?.botUsername).then((ok) => {
              if (ok) setAlertsOn(true);
            });
          }}
        >
          <div className="home-onboardBanner-icon">
            <Icon name="bell-dot" size={18} />
          </div>
          <div className="home-onboardBanner-text">
            <div className="home-onboardBanner-title">{t('home.botAlerts')}</div>
            <div className="home-onboardBanner-sub">{t('home.botAlertsSub')}</div>
          </div>
          <Icon name="arrow-right-01" size={16} />
        </button>
      )}

      {needsOnboarding && (
        <button type="button" className="home-onboardBanner" onClick={goWelcome}>
          <div className="home-onboardBanner-icon">
            <Icon name="award-01" size={18} />
          </div>
          <div className="home-onboardBanner-text">
            <div className="home-onboardBanner-title">{t('home.pickYourLevel')}</div>
            <div className="home-onboardBanner-sub">{t('home.pickYourLevelSub')}</div>
          </div>
          <Icon name="arrow-right-01" size={16} />
        </button>
      )}

      <div className="home-cta-row">
        <button type="button" className="btn" onClick={goCreate}>
          <Icon name="plus-sign" size={16} />
          {t('home.createGame')}
        </button>
        <span className="home-cta-hint">{t('home.createGameSub')}</span>
      </div>

      {rateGames.length > 0 && (
        <section className="section home-rateSection">
          <div className="section-header">
            <h2 className="section-title">
              <span className="section-title-icon">
                <Icon name="award-01" size={16} />
              </span>
              {t('home.ratePlayers')}
            </h2>
          </div>
          {rateGames.map((g) => (
            <button
              key={g.id}
              type="button"
              className="home-rateCard"
              onClick={() => navigate(`/games/${g.id}`)}
              data-analytics-label="home-rate-game"
            >
              <div className="home-rateCard-body">
                <div className="home-rateCard-title">{g.venueName}</div>
                <div className="home-rateCard-sub">{t('home.ratePlayersSub')}</div>
              </div>
              <Icon name="arrow-right-01" size={16} />
            </button>
          ))}
        </section>
      )}

      <div className="stat-strip">
        <div className="stat">
          <div className="stat-value">{openGames.length}</div>
          <div className="stat-label">{t('home.openGames')}</div>
        </div>
        <div className="stat">
          <div className="stat-value">
            {openGames.reduce((sum, g) => sum + (g.spotsTotal - g.participantsCount - (g.reservedCount ?? 0)), 0)}
          </div>
          <div className="stat-label">{t('home.freeSpots')}</div>
        </div>
        <div className="stat">
          <div className="stat-value">{gamesQ.data?.length ?? 0}</div>
          <div className="stat-label">{t('home.allGames')}</div>
        </div>
      </div>

      <section className="section">
        <div className="section-header">
          <h2 className="section-title">
            <span className="section-title-icon">
              <Icon name="calendar-01" size={16} />
            </span>
            {t('home.upcomingGames')}
          </h2>
          <Link to="/games" className="section-action" data-analytics-label="home-see-all">
            {t('home.seeAll')}
            <Icon name="arrow-right-01" size={12} />
          </Link>
        </div>

        {gamesQ.isLoading && (
          <>
            {[0, 1, 2].map((i) => (
              <div key={i} className="card" style={{ marginBottom: 12, height: 130 }}>
                <div className="skeleton" style={{ width: '60%', height: 18, marginBottom: 8 }} />
                <div className="skeleton" style={{ width: '40%', height: 14, marginBottom: 16 }} />
                <div className="skeleton" style={{ width: '100%', height: 32, borderRadius: 10 }} />
              </div>
            ))}
          </>
        )}

        {gamesQ.isError && (
          <div className="error">
            <Icon name="bell-dot" size={16} />
            <span>{(gamesQ.error as Error).message}</span>
          </div>
        )}

        {!gamesQ.isLoading && nextGames.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Icon name="tennis-ball" size={24} />
            </div>
            <div className="empty-state-title">{t('home.noGames')}</div>
            <div className="empty-state-text">
              {t('home.emptyCta', { city })}
            </div>
          </div>
        )}

        {nextGames.map((g) => (
          <GameCard key={g.id} game={g} />
        ))}
      </section>
    </div>
  );
}
